import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./BidCreationTable.css";
import { ReactComponent as Undo } from "../../../../icons/undo.svg";
import GeneratorListModal from "./GeneratorListModal";
import BidCreationFilter from "./BidCreationFilter";

// ─── API types (matches getClassBid response) ───────────────────────
interface ApiBidEntry {
  quantum: number;
  price: number | null;
}

interface ApiSlotRow {
  time_slot__slot: number;
  time_string: string;
  demand_mw: number;
  tap: number;
  class_quantum: number;
  class_vc: number;
  must_run_mw: number;
  demand_met_before: number;
  demand_met_after: number;
  sur_def: number;
  anticipated_dam_rate: number;
  role: string;
  block_buy: ApiBidEntry;
  block_sell: ApiBidEntry;
  single_buy: ApiBidEntry;
  single_sell: ApiBidEntry;
  deficit_buy?: ApiBidEntry;
}

// ─── Transformed row ────────────────────────────────────────────────
interface BidRow {
  rowId: string;
  slotIndex: number;
  timeRange: string;
  demand: number;
  tap: number;
  surDef: number;
  anticipatedRate: number;
  role: string;
  demandMet: number;
  classVol: number;
  classRate: number;
  mustRunMw: number;
  // Block (raw + signed net)
  blockBuyQty: number;
  blockBuyPrice: number | null;
  blockSellQty: number;
  blockSellPrice: number | null;
  blockNetQty: number;
  blockNetPrice: number | null;
  // Single
  singleBuyQty: number;
  singleBuyPrice: number | null;
  singleSellQty: number;
  singleSellPrice: number | null;
  // Deficit Buy
  deficitBuyQty: number | null;
  deficitBuyPrice: number | null;
}

const toFiniteOrNull = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toMagnitude = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

const deriveBlockNet = (entry: ApiSlotRow) => {
  const buyQty = toMagnitude(entry.block_buy?.quantum);
  const sellQty = toMagnitude(entry.block_sell?.quantum);
  const buyPrice = toFiniteOrNull(entry.block_buy?.price);
  const sellPrice = toFiniteOrNull(entry.block_sell?.price);

  if (buyQty === 0 && sellQty === 0) {
    if (buyPrice !== null && sellPrice === null) {
      return { qty: 0, price: Math.abs(buyPrice) };
    }
    if (sellPrice !== null && buyPrice === null) {
      return { qty: 0, price: -Math.abs(sellPrice) };
    }
    return { qty: 0, price: null };
  }

  if (buyQty >= sellQty) {
    return {
      qty: buyQty,
      price: buyPrice === null ? null : Math.abs(buyPrice),
    };
  }

  return {
    qty: -sellQty,
    price: sellPrice === null ? null : -Math.abs(sellPrice),
  };
};

export function transformClassData(entries: ApiSlotRow[]): BidRow[] {
  if (!entries?.length) return [];
  return entries
    .slice()
    .sort((a, b) => a.time_slot__slot - b.time_slot__slot)
    .map((e, idx) => {
      const blockNet = deriveBlockNet(e);
      return {
        rowId: `${e.time_slot__slot}__${e.time_string ?? ""}__${e.role ?? ""}__${idx}`,
        slotIndex: e.time_slot__slot,
        timeRange: e.time_string,
        demand: e.demand_mw,
        tap: e.tap,
        surDef: e.sur_def,
        anticipatedRate: e.anticipated_dam_rate,
        role: e.role,
        demandMet: e.demand_met_before,
        classVol: e.class_quantum,
        classRate: e.class_vc,
        mustRunMw: e.must_run_mw,
        blockBuyQty: e.block_buy?.quantum ?? 0,
        blockBuyPrice: e.block_buy?.price ?? null,
        blockSellQty: e.block_sell?.quantum ?? 0,
        blockSellPrice: e.block_sell?.price ?? null,
        blockNetQty: blockNet.qty,
        blockNetPrice: blockNet.price,
        singleBuyQty: e.single_buy?.quantum ?? 0,
        singleBuyPrice: e.single_buy?.price ?? null,
        singleSellQty: e.single_sell?.quantum ?? 0,
        singleSellPrice: e.single_sell?.price ?? null,
        deficitBuyQty:
          e.deficit_buy != null ? toMagnitude(e.deficit_buy.quantum) : null,
        deficitBuyPrice:
          e.deficit_buy != null ? toFiniteOrNull(e.deficit_buy.price) : null,
      };
    });
}

type BlockSelection =
  | number
  | { value?: number | string; label?: number | string };

const resolveBlockNumber = (block: BlockSelection): number | null => {
  if (typeof block === "number") return Number.isFinite(block) ? block : null;
  if (block && typeof block === "object") {
    const parsed = Number(block.value ?? block.label);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// ─── Props ──────────────────────────────────────────────────────────
interface BidCreationTableProps {
  maxHeight?: string;
  mode?: "normal" | "edit";
  apiData?: any;
  classGroupedData?: any[];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: (data: any) => void;
  loading?: boolean;
  selectedBlock?: BlockSelection[];
  modalFilter?: boolean;
  setModalFilter?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface EditingCell {
  slot: number;
  field: string;
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  return (
    <span
      className={`text-[10px] leading-none ${active ? "text-[#34656D]" : "text-gray-300"}`}
    >
      {active && dir === "desc" ? "▼" : "▲"}
    </span>
  );
}

const fmtNum = (v: any, decimals = 0): string => {
  if (v === null || v === undefined) return "–";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
};

type BidSide = "buy" | "sell";

const signedMagnitude = (v: unknown, side: BidSide): number | null => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const magnitude = Math.abs(n);
  return side === "buy" ? magnitude : -magnitude;
};

const fmtSignedPrice = (v: unknown, side: BidSide): React.ReactNode => {
  if (v === null || v === undefined) {
    return <span className="bid_mr_dash">–</span>;
  }
  const signed = signedMagnitude(v, side);
  if (signed === null) {
    return <span className="bid_mr_dash">–</span>;
  }
  if (signed === 0) {
    return <span className="text-gray-300">0</span>;
  }
  return <span className="bid_rate_value">₹{Math.abs(signed).toFixed(2)}</span>;
};

const fmtSignedQty = (v: unknown, side: BidSide): React.ReactNode => {
  const signed = signedMagnitude(v, side);
  if (signed === null || signed === 0) {
    return <span className="text-gray-300">0</span>;
  }
  return Math.abs(signed).toFixed(2);
};

const RATE_FIELDS_VALIDATED = new Set(["classRate"]);

// ─── Component ──────────────────────────────────────────────────────
export function BidCreationTable({
  maxHeight = "calc(-300px + 100vh)",
  mode = "normal",
  apiData,
  classGroupedData = [],
  setModalOpen,
  handleSubmit,
  loading = false,
  selectedBlock = [],
  modalFilter = false,
  setModalFilter,
}: BidCreationTableProps) {
  const [generatorModalOpen, setGeneratorModalOpen] = useState(false);
  const classDataMap = useMemo(() => {
    const raw = apiData?.data ?? apiData ?? {};
    const map: Record<string, ApiSlotRow[]> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (Array.isArray(value)) map[key] = value as ApiSlotRow[];
    }
    return map;
  }, [apiData]);

  const sortedClassKeys = useMemo(
    () => Object.keys(classDataMap).sort((a, b) => Number(a) - Number(b)),
    [classDataMap],
  );

  const classVcRanges = useMemo(() => {
    // per-class max vc, sorted by class key ascending
    const perClassMax: { key: string; max: number }[] = [];
    for (const [key, entries] of Object.entries(classDataMap)) {
      let max = -Infinity;
      for (const e of entries) {
        const vc = e.class_vc;
        if (vc != null && Number.isFinite(vc) && vc > max) max = vc;
      }
      if (max !== -Infinity) perClassMax.push({ key, max });
    }
    perClassMax.sort((a, b) => Number(a.key) - Number(b.key));

    const ranges: Record<string, { min: number; max: number }> = {};
    perClassMax.forEach(({ key, max }, idx) => {
      // class 1 min = its own max (flat range); each subsequent class starts from prev class max
      const min = idx === 0 ? 0 : perClassMax[idx - 1].max;
      ranges[key] = { min, max };
    });
    return ranges;
  }, [classDataMap]);

  const [selectedClassKey, setSelectedClassKey] = useState<string>("");

  useEffect(() => {
    if (
      sortedClassKeys.length > 0 &&
      !sortedClassKeys.includes(selectedClassKey)
    )
      setSelectedClassKey(sortedClassKeys[0]);
  }, [sortedClassKeys, selectedClassKey]);

  const allRows = useMemo(
    () => transformClassData(classDataMap[selectedClassKey] ?? []),
    [classDataMap, selectedClassKey],
  );

  const hasDeficitBuy = useMemo(
    () =>
      selectedClassKey === "5" && allRows.some((r) => r.deficitBuyQty !== null),
    [allRows, selectedClassKey],
  );

  const visibleRows = useMemo(() => {
    if (!Array.isArray(selectedBlock) || selectedBlock.length === 0)
      return allRows;
    const selected = selectedBlock
      .map(resolveBlockNumber)
      .filter((s): s is number => s !== null && s >= 1 && s <= 96);
    if (!selected.length) return allRows;
    const set = new Set(selected);
    return allRows.filter((r) => set.has(r.slotIndex));
  }, [allRows, selectedBlock]);

  type SortKey =
    | { kind: "slot" }
    | { kind: "frozen"; field: "demand" | "tap" | "surDef" }
    | { kind: "data"; field: string };
  type SortState = { key: SortKey; dir: "asc" | "desc" } | null;
  const [sortState, setSortState] = useState<SortState>(null);

  const sortKey = sortState?.key ?? null;
  const sortDir = sortState?.dir ?? "asc";

  const toggleSort = useCallback((key: SortKey) => {
    setSortState((prev) => {
      const same =
        prev !== null && JSON.stringify(prev.key) === JSON.stringify(key);
      if (same) {
        return { key, dir: prev!.dir === "asc" ? "desc" : "asc" };
      }
      // First click on a new column → default to descending so the change is
      // visible even when the data is already in natural ascending order.
      return { key, dir: "desc" };
    });
  }, []);

  const rows = useMemo(() => {
    if (!sortState) return visibleRows;
    const { key, dir } = sortState;

    const getSortValue = (row: BidRow): unknown => {
      if (key.kind === "slot") return row.slotIndex;
      if (key.kind === "frozen") return row[key.field];
      return (row as any)[key.field];
    };

    const normalizeSortValue = (value: unknown): number | string => {
      if (value === null || value === undefined || value === "")
        return Number.NEGATIVE_INFINITY;
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
      }
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      return String(value).toLowerCase();
    };

    return [...visibleRows].sort((a, b) => {
      const av = normalizeSortValue(getSortValue(a));
      const bv = normalizeSortValue(getSortValue(b));

      let cmp = 0;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      if (cmp === 0) {
        cmp = a.slotIndex - b.slotIndex;
      }

      if (cmp === 0) {
        cmp = a.rowId.localeCompare(b.rowId);
      }

      return dir === "asc" ? cmp : -cmp;
    });
  }, [visibleRows, sortState]);

  const totalSlots = rows.length;

  // Edit state
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [draftValue, setDraftValue] = useState("");
  const [vcError, setVcError] = useState<string | null>(null);
  const isEditMode = mode === "edit";

  const valueKey = useCallback(
    (slot: number, field: string) => `${selectedClassKey}__${slot}__${field}`,
    [selectedClassKey],
  );

  const getOriginal = useCallback(
    (slotIdx: number, field: string) => {
      const row = allRows.find((r) => r.slotIndex === slotIdx);
      return (row as any)?.[field] ?? 0;
    },
    [allRows],
  );

  const startEdit = (slot: number, field: string) => {
    if (!isEditMode) return;
    if (editingCell?.slot === slot && editingCell?.field === field) return;
    const k = valueKey(slot, field);
    const original = getOriginal(slot, field);
    const currentValue =
      localValues[k] !== undefined ? localValues[k] : original;
    setDraftValue(currentValue == null ? "" : String(currentValue));
    setEditingCell({ slot, field });
  };

  const confirmEdit = () => {
    if (!editingCell) return;
    const { slot, field } = editingCell;
    const k = valueKey(slot, field);
    const trimmed = draftValue.trim();
    const valueToSave =
      trimmed === "" ? "" : isNaN(Number(trimmed)) ? trimmed : Number(trimmed);
    const original = getOriginal(slot, field);
    const prev = Object.prototype.hasOwnProperty.call(localValues, k)
      ? localValues[k]
      : original;

    // VC range validation for rate fields
    if (
      RATE_FIELDS_VALIDATED.has(field) &&
      typeof valueToSave === "number" &&
      Number.isFinite(valueToSave)
    ) {
      const range = classVcRanges[selectedClassKey];
      if (range) {
        if (valueToSave < range.min || valueToSave > range.max) {
          setVcError(
            `Value ${valueToSave.toFixed(2)} is outside VC range [${range.min.toFixed(2)} – ${range.max.toFixed(2)}] for Class ${selectedClassKey}.`,
          );
          return;
        }
      }
    }

    setVcError(null);
    if (valueToSave === prev) {
      setEditingCell(null);
      setDraftValue("");
      return;
    }
    setLocalValues((p) => ({ ...p, [k]: valueToSave }));
    setEditingCell(null);
    setDraftValue("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setDraftValue("");
  };

  const revertCell = (slot: number, field: string) => {
    const k = valueKey(slot, field);
    setLocalValues((p) => {
      if (!(k in p)) return p;
      const { [k]: _, ...rest } = p;
      return rest;
    });
  };

  const revertAll = () => {
    if (!Object.keys(localValues).length) return;
    setLocalValues({});
    cancelEdit();
  };

  const applyBulkChanges = useCallback(
    (
      changes: Array<{
        slotIndex: number;
        classKey: string;
        field: string;
        value: number;
      }>,
      vcRanges?: Record<string, { min: number; max: number }>,
    ) => {
      if (!changes.length) return;

      const violations: string[] = [];
      const valid: typeof changes = [];

      for (const c of changes) {
        const range = vcRanges?.[c.classKey];
        if (
          range &&
          RATE_FIELDS_VALIDATED.has(c.field) &&
          Number.isFinite(c.value) &&
          (c.value < range.min || c.value > range.max)
        ) {
          violations.push(
            `Class ${c.classKey} Slot ${c.slotIndex}: ${c.value.toFixed(2)} outside [${range.min.toFixed(2)} – ${range.max.toFixed(2)}]`,
          );
        } else {
          valid.push(c);
        }
      }

      if (violations.length) {
        setVcError(
          `${violations.length} value(s) rejected (outside VC range):\n• ${violations.slice(0, 5).join("\n• ")}${violations.length > 5 ? `\n…and ${violations.length - 5} more` : ""}`,
        );
      } else {
        setVcError(null);
      }

      if (!valid.length) return;
      setLocalValues((prev) => {
        const next = { ...prev };
        for (const { slotIndex, classKey, field, value } of valid) {
          next[`${classKey}__${slotIndex}__${field}`] = value;
        }
        return next;
      });
    },
    [],
  );

  const uniqueSlots = useMemo(() => allRows.map((r) => r.slotIndex), [allRows]);

  const handleUpdateClick = () => {
    if (!Object.keys(localValues).length) return;
    handleSubmit?.(localValues);
  };

  useEffect(() => {
    setEditingCell(null);
  }, [mode]);
  useEffect(() => {
    setLocalValues({});
    setEditingCell(null);
  }, [apiData]);

  const localSize = Object.keys(localValues).length;

  const formatEditedValue = useCallback((field: string, value: any) => {
    switch (field) {
      case "blockNetQty": {
        const n = Number(value);
        return fmtSignedQty(value, n < 0 ? "sell" : "buy");
      }
      case "blockNetPrice": {
        const n = Number(value);
        return fmtSignedPrice(value, n < 0 ? "sell" : "buy");
      }
      case "singleBuyQty":
      case "blockBuyQty":
        return fmtSignedQty(value, "buy");
      case "singleSellQty":
      case "blockSellQty":
        return fmtSignedQty(value, "sell");
      case "singleBuyPrice":
      case "blockBuyPrice":
        return fmtSignedPrice(value, "buy");
      case "singleSellPrice":
      case "blockSellPrice":
        return fmtSignedPrice(value, "sell");
      case "classRate":
        return fmtNum(value, 2);
      default:
        return fmtNum(value);
    }
  }, []);

  // Rate-only editing: mirrors Classes pattern (only specific fields editable).
  const EDITABLE_FIELDS = new Set<string>([
    "classRate",
    "singleBuyPrice",
    "blockNetPrice",
    "singleSellPrice",
  ]);

  // Render editable cell
  const renderCell = (
    row: BidRow,
    field: string,
    displayText: React.ReactNode,
    tdClass: string,
  ) => {
    const k = valueKey(row.slotIndex, field);
    const original = (row as any)[field];
    const differs =
      Object.prototype.hasOwnProperty.call(localValues, k) &&
      localValues[k] !== original;
    const isEditing =
      editingCell?.slot === row.slotIndex && editingCell?.field === field;
    const canEdit = isEditMode && EDITABLE_FIELDS.has(field);

    return (
      <td
        key={field}
        className={`bid_td ${tdClass} ${canEdit ? "cursor-pointer" : ""} relative`}
        onClick={() => {
          if (canEdit && !isEditing) startEdit(row.slotIndex, field);
        }}
      >
        {differs && !isEditing && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              revertCell(row.slotIndex, field);
            }}
            className="absolute top-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-bl text-[10px] px-1.5 py-[1px] leading-tight shadow z-[9]"
            title="Revert"
          >
            ↺
          </button>
        )}
        {!isEditing && (
          <span className={differs ? "text-[#F1935C] font-semibold" : ""}>
            {localValues[k] !== undefined
              ? formatEditedValue(field, localValues[k])
              : displayText}
          </span>
        )}
        {isEditing && (
          <InlineEditor
            draftValue={draftValue}
            setDraftValue={setDraftValue}
            onConfirm={confirmEdit}
            onCancel={cancelEdit}
            step={field === "classRate" ? "0.001" : "0.01"}
          />
        )}
      </td>
    );
  };

  // Sort helper
  const sortTh = (field: string, label: string, extraClass = "") => {
    const isActive =
      sortKey?.kind === "data" && (sortKey as any).field === field;
    return (
      <th
        className={`bid_th_sub ${extraClass} cursor-pointer select-none`}
        onClick={() => toggleSort({ kind: "data", field })}
      >
        <span className="inline-flex items-center gap-1">
          {label} <SortIcon active={isActive} dir={sortDir} />
        </span>
      </th>
    );
  };

  if (sortedClassKeys.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  const vcRange = classVcRanges[selectedClassKey];

  return (
    <div className="relative flex flex-col rounded-xl border-none h-full">
      {/* ── Table ── */}
      <div
        className={`overflow-auto flex-1 min-h-0 border-none bid_scroll_container ${isEditMode ? "rounded-b-none" : ""}`}
        style={{ maxHeight }}
      >
        <table id="bidCreationTable" className="bid_creation_table">
          <thead className="sticky top-0 z-50">
            {/* Row 1: group headers */}
            <tr className=" ">
              <th className="bid_th_lgb" colSpan={5}>
                <span className="bid_lgb_label">Load Generation Balance</span>
              </th>
              {/* ── Class toggle tabs ── */}
              <th className="bid_th_corner" colSpan={3}>
                <div className="bid_class_tabs">
                  <div className="flex flex-col leading-tight mr-8">
                    <span className="bid_class_tabs_label text-left mb-1">
                      Selected Class
                    </span>
                    {vcRange && (
                      <span className="bid_class_vc_label">
                        VC (₹/kWh): {vcRange.min.toFixed(2)}-
                        {vcRange.max.toFixed(2)}
                      </span>
                    )}
                  </div>
                  {sortedClassKeys.map((classKey) => (
                    <button
                      key={classKey}
                      className={`bid_class_tab ${classKey === selectedClassKey ? "bid_class_tab_active" : ""}`}
                      onClick={() => setSelectedClassKey(classKey)}
                    >
                      C{classKey}
                    </button>
                  ))}
                </div>
              </th>
              <th className="bid_th_group bid_th_group_section" colSpan={2}>
                Single Bid (Buy)
                <br />
                <span
                  className="bid_view_generators_link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeneratorModalOpen(true);
                  }}
                >
                  View all generators
                </span>
              </th>
              <th className="bid_th_group bid_th_group_mid" colSpan={2}>
                Block Bid
              </th>
              {hasDeficitBuy && (
                <th className="bid_th_group bid_th_group_mid" colSpan={2}>
                  Deficit Buy
                </th>
              )}
              <th className="bid_th_group bid_th_group_section" colSpan={2}>
                Single Bid (Sell)
                <br />
                <span
                  className="bid_view_generators_link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeneratorModalOpen(true);
                  }}
                >
                  View all generators
                </span>
              </th>
            </tr>

            {/* Row 2: sub-column headers */}
            <tr>
              {/* Frozen */}
              <th
                className="bid_th_frozen bid_th_frozen_timeslot cursor-pointer select-none"
                onClick={() => toggleSort({ kind: "slot" })}
              >
                <span className="inline-flex items-center gap-1">
                  Time Slots{" "}
                  <SortIcon active={sortKey?.kind === "slot"} dir={sortDir} />
                </span>
              </th>
              <th
                className="bid_th_frozen bid_th_frozen_col cursor-pointer select-none"
                onClick={() => toggleSort({ kind: "frozen", field: "demand" })}
                style={{ left: "var(--bid-col1-w)" }}
              >
                <span className="inline-flex items-center gap-1">
                  Demand{" "}
                  <SortIcon
                    active={
                      sortKey?.kind === "frozen" &&
                      (sortKey as any).field === "demand"
                    }
                    dir={sortDir}
                  />
                </span>
              </th>
              <th
                className="bid_th_frozen bid_th_frozen_col cursor-pointer select-none"
                onClick={() => toggleSort({ kind: "frozen", field: "tap" })}
                style={{ left: "calc(var(--bid-col1-w) + var(--bid-col2-w))" }}
              >
                <span className="inline-flex items-center gap-1">
                  DC{" "}
                  <SortIcon
                    active={
                      sortKey?.kind === "frozen" &&
                      (sortKey as any).field === "tap"
                    }
                    dir={sortDir}
                  />
                </span>
              </th>
              <th
                className="bid_th_frozen bid_th_frozen_col cursor-pointer select-none"
                onClick={() => toggleSort({ kind: "frozen", field: "surDef" })}
                style={{
                  left: "calc(var(--bid-col1-w) + var(--bid-col2-w) + var(--bid-col3-w))",
                }}
              >
                <span className="inline-flex items-center gap-1">
                  Sur/Def{" "}
                  <SortIcon
                    active={
                      sortKey?.kind === "frozen" &&
                      (sortKey as any).field === "surDef"
                    }
                    dir={sortDir}
                  />
                </span>
              </th>
              <th
                className="bid_th_frozen bid_th_frozen_col bid_th_frozen_last"
                style={{
                  left: "calc(var(--bid-col1-w) + var(--bid-col2-w) + var(--bid-col3-w) + var(--bid-col4-w))",
                  minWidth: "var(--bid-col5-w)",
                }}
              >
                Anticipated Rate
              </th>

              {/* Class data */}
              {sortTh("demandMet", "Demand Met")}
              {sortTh("classVol", "Class Vol")}
              {sortTh("classRate", "Class Rate", "bid_th_sub_class_end")}

              {/* Buy Bid */}
              {sortTh("singleBuyQty", "Quantum", "bid_th_sub_buy")}
              {sortTh(
                "singleBuyPrice",
                "Rate",
                "bid_th_sub_buy bid_th_sub_buy_end",
              )}

              {/* Block Bid */}
              {sortTh("blockNetQty", "Quantum", "bid_th_sub_block")}
              {sortTh(
                "blockNetPrice",
                "Rate",
                "bid_th_sub_block bid_th_sub_block_end",
              )}

              {/* Deficit Buy */}
              {hasDeficitBuy &&
                sortTh("deficitBuyQty", "Quantum", "bid_th_sub_block")}
              {hasDeficitBuy &&
                sortTh(
                  "deficitBuyPrice",
                  "Rate",
                  "bid_th_sub_block bid_th_sub_block_end",
                )}

              {/* Sell Bid */}
              {sortTh("singleSellQty", "Quantum", "bid_th_sub_sell")}
              {sortTh(
                "singleSellPrice",
                "Rate",
                "bid_th_sub_sell bid_th_sub_last",
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.rowId} className="bid_tr_body">
                {/* Frozen */}
                <td className="bid_td_frozen bid_td_frozen_timeslot">
                  Slot {row.slotIndex}: ({row.timeRange})
                </td>
                <td
                  className="bid_td_frozen bid_td_frozen_col"
                  style={{ left: "var(--bid-col1-w)" }}
                >
                  {fmtNum(row.demand)}
                </td>
                <td
                  className="bid_td_frozen bid_td_frozen_col"
                  style={{
                    left: "calc(var(--bid-col1-w) + var(--bid-col2-w))",
                  }}
                >
                  {fmtNum(row.tap)}
                </td>
                <td
                  className="bid_td_frozen bid_td_frozen_col"
                  style={{
                    left: "calc(var(--bid-col1-w) + var(--bid-col2-w) + var(--bid-col3-w))",
                  }}
                >
                  <span>{fmtNum(row.surDef)}</span>
                </td>
                <td
                  className="bid_td_frozen bid_td_frozen_col bid_td_frozen_last"
                  style={{
                    left: "calc(var(--bid-col1-w) + var(--bid-col2-w) + var(--bid-col3-w) + var(--bid-col4-w))",
                    minWidth: "var(--bid-col5-w)",
                  }}
                >
                  {fmtNum(row.anticipatedRate)}
                </td>

                {/* Class */}
                {renderCell(
                  row,
                  "demandMet",
                  fmtNum(row.demandMet),
                  "bid_td_class",
                )}
                {renderCell(
                  row,
                  "classVol",
                  fmtNum(row.classVol),
                  "bid_td_class",
                )}
                {renderCell(
                  row,
                  "classRate",
                  fmtNum(row.classRate, 2),
                  "bid_td_class bid_td_class_end",
                )}

                {/* Buy Bid (single buy) */}
                {renderCell(
                  row,
                  "singleBuyQty",
                  fmtSignedQty(row.singleBuyQty, "buy"),
                  "bid_td_buy",
                )}
                {renderCell(
                  row,
                  "singleBuyPrice",
                  fmtSignedPrice(row.singleBuyPrice, "buy"),
                  "bid_td_buy bid_td_buy_end",
                )}

                {/* Block Bid (signed net) */}
                {renderCell(
                  row,
                  "blockNetQty",
                  fmtSignedQty(
                    row.blockNetQty,
                    row.blockNetQty < 0 ? "sell" : "buy",
                  ),
                  "bid_td_block",
                )}
                {renderCell(
                  row,
                  "blockNetPrice",
                  fmtSignedPrice(
                    row.blockNetPrice,
                    (row.blockNetPrice ?? 0) < 0 ? "sell" : "buy",
                  ),
                  "bid_td_block bid_td_block_end",
                )}

                {/* Deficit Buy */}
                {hasDeficitBuy &&
                  renderCell(
                    row,
                    "deficitBuyQty",
                    fmtNum(row.deficitBuyQty ?? 0),
                    "bid_td_block",
                  )}
                {hasDeficitBuy &&
                  renderCell(
                    row,
                    "deficitBuyPrice",
                    row.deficitBuyPrice != null ? (
                      <span className="bid_rate_value">
                        ₹{row.deficitBuyPrice.toFixed(2)}
                      </span>
                    ) : (
                      <span className="bid_mr_dash">–</span>
                    ),
                    "bid_td_block bid_td_block_end",
                  )}

                {/* Sell Bid (single sell) */}
                {renderCell(
                  row,
                  "singleSellQty",
                  fmtSignedQty(row.singleSellQty, "sell"),
                  "bid_td_sell",
                )}
                {renderCell(
                  row,
                  "singleSellPrice",
                  fmtSignedPrice(row.singleSellPrice, "sell"),
                  "bid_td_sell bid_td_last",
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* VC validation error – floating toast, doesn't affect layout */}
      {isEditMode && vcError && (
        <div className="absolute bottom-[90px] left-1/2 -translate-x-1/2 z-[200] flex items-start gap-2 px-4 py-2 rounded-lg border border-red-300 bg-red-50 shadow-lg text-[11px] text-red-700 max-w-[520px] w-max">
          <pre className="whitespace-pre-wrap font-sans">{vcError}</pre>
          <button
            type="button"
            onClick={() => setVcError(null)}
            className="text-red-400 hover:text-red-600 shrink-0 text-base leading-none mt-0.5"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Edit instructions */}
      {isEditMode && (
        <div className="px-4 py-2 border-t border-[#DFEAF2] text-[11px] text-[#9B98AF] leading-relaxed bg-[#FAFAFA]">
          <span className="font-semibold text-[#525062]">How to edit: </span>
          Click any cell → type new value → press{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">
            Enter
          </kbd>{" "}
          or <span className="text-green-600 font-medium">✓</span> to confirm,{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">
            Esc
          </kbd>{" "}
          to cancel. &nbsp;|&nbsp; Changed values show in{" "}
          <span className="text-[#F1935C] font-semibold">orange</span>.
        </div>
      )}

      {/* Footer action bar */}
      {isEditMode && (
        <div className="mod-footer-bar flex justify-between items-center">
          <div className="mod-footer-metrics">
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Total Slots :</span>
              <span className="mod-footer-metric-value">{totalSlots}</span>
            </div>
            <div className="mod-footer-divider" aria-hidden="true" />
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Modifications :</span>
              <span className="mod-footer-metric-value mod-footer-metric-value--accent">
                +{localSize}
              </span>
            </div>
          </div>
          <div className="mod-footer-actions">
            <button
              onClick={() => setModalOpen(false)}
              className="mod-footer-cancel"
            >
              Cancel
            </button>
            <button
              onClick={revertAll}
              disabled={localSize === 0 || loading}
              className="mod-footer-revert"
            >
              <Undo className="w-4 h-4" /> Revert
            </button>
            <button
              onClick={handleUpdateClick}
              className="mod-footer-update"
              disabled={localSize === 0 || loading}
            >
              {loading ? "Saving..." : "Update & Review"}
            </button>
          </div>
        </div>
      )}

      {/* Generator List Modal */}
      <GeneratorListModal
        isOpen={generatorModalOpen}
        onClose={() => setGeneratorModalOpen(false)}
        classNumber={Number(selectedClassKey) || 1}
        apiData={classGroupedData}
      />

      {/* Batch Customize Modal */}
      <BidCreationFilter
        isOpen={modalFilter}
        onClose={() => setModalFilter?.(false)}
        uniqueBlocks={uniqueSlots}
        classKeys={sortedClassKeys}
        loading={loading}
        classVcRanges={classVcRanges}
        onLocalApply={(changes) => applyBulkChanges(changes, classVcRanges)}
      />
    </div>
  );
}

// ─── Inline editor ──────────────────────────────────────────────────
function InlineEditor({
  draftValue,
  setDraftValue,
  onConfirm,
  onCancel,
  step = "any",
}: {
  draftValue: string;
  setDraftValue: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  step?: string;
}) {
  return (
    <div className="absolute z-[9999] inset-0 flex items-center justify-center">
      <div className="flex border border-gray-300 shadow-lg bg-white rounded">
        <input
          autoFocus
          step={step}
          inputMode="decimal"
          type="number"
          className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onConfirm();
            else if (e.key === "Escape") onCancel();
          }}
          onBlur={(e) => {
            if (
              !e.currentTarget.parentElement?.contains(e.relatedTarget as Node)
            )
              onCancel();
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onConfirm();
          }}
          className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
          title="Save"
        >
          ✓
        </button>
      </div>
    </div>
  );
}
